import React from 'react';
import { motion } from 'framer-motion';
import { Circle, Play, Square, Globe, Edit, GitBranch, Code, Webhook, Merge, Split, FunctionSquare, Filter, Repeat, GitFork, Clock, AlertCircle, Workflow, Cpu } from 'lucide-react';

const iconMap = {
  play: Play,
  square: Square,
  globe: Globe,
  edit: Edit,
  'git-branch': GitBranch,
  code: Code,
  webhook: Webhook,
  merge: Merge,
  split: Split,
  'function-square': FunctionSquare,
  filter: Filter,
  repeat: Repeat,
  'git-fork': GitFork,
  clock: Clock,
  'alert-circle': AlertCircle,
  workflow: Workflow,
  cpu: Cpu,
};

const NodeLibrary = ({ nodeTypes = [], availableTypes = [] }) => {
  const [touchDragData, setTouchDragData] = React.useState(null);

  const onDragStart = (event, nodeType, nodeData) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('nodeData', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  // Touch handlers for mobile
  const onTouchStart = (event, nodeType, nodeData) => {
    const touch = event.touches[0];
    setTouchDragData({
      nodeType,
      nodeData,
      startX: touch.clientX,
      startY: touch.clientY,
    });
  };

  const onTouchEnd = (event, nodeType, nodeData) => {
    if (!touchDragData) return;

    const touch = event.changedTouches[0];
    const canvas = document.querySelector('[data-testid="node-editor"]');

    if (canvas) {
      const canvasRect = canvas.getBoundingClientRect();
      const x = touch.clientX;
      const y = touch.clientY;

      // Check if touch ended on canvas
      if (
        x >= canvasRect.left &&
        x <= canvasRect.right &&
        y >= canvasRect.top &&
        y <= canvasRect.bottom
      ) {
        // Dispatch custom event for mobile drop
        const dropEvent = new CustomEvent('mobileNodeDrop', {
          detail: {
            nodeType: touchDragData.nodeType,
            nodeData: touchDragData.nodeData,
            x: x - canvasRect.left,
            y: y - canvasRect.top,
          },
        });
        canvas.dispatchEvent(dropEvent);
      }
    }

    setTouchDragData(null);
  };

  const displayedNodes = availableTypes.length > 0
    ? nodeTypes.filter((n) => availableTypes.includes(n.type))
    : nodeTypes;

  return (
    <div className="space-y-1.5 sm:space-y-2" data-testid="node-library">
      <h3 className="text-xs sm:text-sm font-heading font-bold text-text-primary mb-2 sm:mb-3">Nodos Disponibles</h3>
      {displayedNodes.map((node, index) => {
        const IconComponent = iconMap[node.icon] || Circle;
        return (
          <motion.div
            key={node.type}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            draggable
            onDragStart={(e) => onDragStart(e, node.type, node)}
            onTouchStart={(e) => onTouchStart(e, node.type, node)}
            onTouchEnd={(e) => onTouchEnd(e, node.type, node)}
            className="glass p-2 sm:p-3 rounded-lg cursor-grab active:cursor-grabbing hover:border-primary/50 transition-all group touch-none"
            whileHover={{ scale: 1.02 }}
            data-testid={`node-type-${node.type}`}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform"
                style={{ backgroundColor: node.color }}
              >
                <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs sm:text-sm font-bold text-text-primary truncate">{node.label}</div>
                <div className="text-[10px] sm:text-xs text-text-muted truncate">{node.description}</div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default NodeLibrary;